import React, { useState, useCallback } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { precompileAndStoreTemplates } from '../../wa-tools/handlebars-manager.js';
import LogViewer from '../shared/LogViewer.jsx';
import '../admin/admin-styles.css';

/**
 * Onglet pour gérer les templates Handlebars
 * - Pré-compilation des templates au niveau frontend
 * - Synchronisation vers la table hb_compiled
 * - Statut et logs de la synchronisation
 */
export default function AdminHandlebarsTab() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [logMessages, setLogMessages] = useState([]);
  const [syncResult, setSyncResult] = useState(null);

  // Callback pour ajouter un message au log
  const addLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogMessages((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  /**
   * Déclenche la pré-compilation et la synchronisation des templates
   */
  const handleSyncTemplates = async () => {
    try {
      setLoading(true);
      setLogMessages([]);
      setSyncResult(null);

      addLog('🚀 Démarrage de la synchronisation des templates Handlebars...');

      // Appeler la fonction de pré-compilation avec callback de progression
      // Pas de filtrage de langue/type pour une synchro complète
      const result = await precompileAndStoreTemplates('FR', null, addLog);

      setSyncResult(result);

      if (result.success) {
        addLog(`✅ Synchronisation réussie: ${result.compiled - result.failed}/${result.total} templates`);
      } else {
        addLog(`⚠️ Synchronisation terminée avec ${result.failed} erreur(s)`);
      }

    } catch (err) {
      addLog(`❌ Erreur fatale: ${err.message}`);
      setSyncResult({ success: false, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-handlebars-tab">
      <section className="admin-section">
        <h3>{t('ADMIN', 'HB_SYNC_TITLE', 'Synchronisation des templates Handlebars')}</h3>
        <p className="admin-description">
          {t('ADMIN', 'HB_SYNC_DESC', 'Pré-compile les templates source depuis tab_actions, ia_calls et article_rag_actions au niveau frontend, puis les synchronise vers la table hb_compiled.')}
        </p>

        <button
          onClick={handleSyncTemplates}
          disabled={loading}
          className="admin-btn admin-btn-primary"
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              {t('ADMIN', 'HB_SYNCING', 'Synchronisation en cours...')}
            </>
          ) : (
            t('ADMIN', 'HB_SYNC_BTN', 'Pré-compiler et synchroniser les templates')
          )}
        </button>

        {/* Résumé de la synchronisation */}
        {syncResult && !loading && (
          <div className={`admin-status ${syncResult.success ? 'success' : 'error'}`}>
            <h4>{t('ADMIN', 'HB_SYNC_RESULT', 'Résultat de la synchronisation')}</h4>
            <div className="status-details">
              <p>
                <strong>{t('ADMIN', 'HB_TOTAL', 'Templates trouvés:')} </strong>
                {syncResult.total || 0}
              </p>
              <p>
                <strong>{t('ADMIN', 'HB_COMPILED', 'Templates pré-compilés:')} </strong>
                <span className={syncResult.failed > 0 ? 'warning' : 'success'}>
                  {(syncResult.compiled || 0) - (syncResult.failed || 0)}
                </span>
              </p>
              {syncResult.failed > 0 && (
                <p>
                  <strong>{t('ADMIN', 'HB_FAILED', 'Templates en erreur:')} </strong>
                  <span className="error">{syncResult.failed}</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Affichage des logs */}
        <LogViewer 
          title={t('ADMIN', 'HB_SYNC_LOG', 'Log de synchronisation')}
          messages={logMessages}
          maxHeight={400}
          autoScroll={true}
        />
      </section>
    </div>
  );
}
